import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Plus,
  Trash2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Package,
} from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  tip: "intrare",
  data: todayISO(),
  cantitate: "",
  nr_lot: "",
  aviz: "",
  firma: "",
  referinta: "",
  observatii: "",
};

export default function StockFisa() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stock, setStock] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stocks/${id}/movements`);
      setStock(data.stock);
      setRows(data.movements || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la încărcare");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const totals = useMemo(() => {
    let intrari = 0, iesiri = 0;
    for (const r of rows) {
      const q = Number(r.cantitate) || 0;
      if (r.tip === "intrare") intrari += q; else iesiri += q;
    }
    return { intrari, iesiri };
  }, [rows]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cantitate || Number(form.cantitate) <= 0) {
      toast.error("Introdu o cantitate validă");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/stocks/${id}/movements`, {
        ...form,
        cantitate: parseFloat(form.cantitate),
      });
      toast.success(form.tip === "intrare" ? "Intrare înregistrată" : "Ieșire înregistrată");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Ștergi mișcarea din ${m.data} (${m.tip} — ${m.cantitate} ${stock?.unitate || ""})?`)) return;
    try {
      await api.delete(`/stocks/${id}/movements/${m.id}`);
      toast.success("Mișcare ștearsă. Stocul a fost recalculat.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  const exportExcel = async () => {
    try {
      const url = `${API_BASE}/stocks/${id}/fisa/export`;
      const res = await fetch(url, {
        credentials: "include",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Export eșuat");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `fisa_magazie_${(stock?.denumire || "stoc").replace(/[^A-Za-z0-9._-]+/g, "_")}.xlsx`;
      link.click();
    } catch {
      toast.error("Nu s-a putut descărca fișa");
    }
  };

  return (
    <div className="space-y-6" data-testid="stock-fisa-page">
      <div>
        <Link
          to="/stocuri"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          data-testid="fisa-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Înapoi la stocuri
        </Link>
      </div>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Fișă de magazie</p>
          <h1 className="font-display font-black text-4xl tracking-tight mt-2 flex items-center gap-3">
            <Package className="w-8 h-8 text-gold-deep" />
            {stock?.denumire || "—"}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span>U.M.: <b className="text-foreground">{stock?.unitate || "—"}</b></span>
            <span>•</span>
            <span>Lot: <b className="text-foreground">{stock?.aviz_lot || "—"}</b></span>
            <span>•</span>
            <span>Stoc curent: <b className={`${(stock?.cantitate ?? 0) <= (stock?.prag_alarma ?? 0) && (stock?.prag_alarma ?? 0) > 0 ? "text-red-600" : "text-foreground"}`}>{stock?.cantitate ?? 0} {stock?.unitate || ""}</b></span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportExcel} data-testid="fisa-export-btn">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
          {isAdmin && (
            <Button
              onClick={() => { setForm({ ...EMPTY, tip: "intrare" }); setOpen(true); }}
              className="btn-gold hover:btn-gold"
              data-testid="fisa-add-intrare-btn"
            >
              <ArrowDownToLine className="w-4 h-4 mr-2" /> Intrare
            </Button>
          )}
          {isAdmin && (
            <Button
              onClick={() => { setForm({ ...EMPTY, tip: "iesire" }); setOpen(true); }}
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              data-testid="fisa-add-iesire-btn"
            >
              <ArrowUpFromLine className="w-4 h-4 mr-2" /> Ieșire
            </Button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white border border-border rounded-md p-4">
          <p className="label-eyebrow">Total intrări</p>
          <p className="text-2xl font-bold text-emerald-700 mt-1">
            {totals.intrari.toFixed(2)} <span className="text-sm font-normal">{stock?.unitate || ""}</span>
          </p>
        </div>
        <div className="bg-white border border-border rounded-md p-4">
          <p className="label-eyebrow">Total ieșiri</p>
          <p className="text-2xl font-bold text-red-700 mt-1">
            {totals.iesiri.toFixed(2)} <span className="text-sm font-normal">{stock?.unitate || ""}</span>
          </p>
        </div>
        <div className="bg-white border border-border rounded-md p-4">
          <p className="label-eyebrow">Mișcări înregistrate</p>
          <p className="text-2xl font-bold mt-1">{rows.length}</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Se încarcă...</p>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-border rounded-md p-12 text-center">
          <Package className="w-10 h-10 mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium">Nicio mișcare înregistrată încă</p>
          <p className="text-sm text-muted-foreground mt-1">
            Fișa se completează automat când creezi PV-uri sau adaugi manual o intrare.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-md overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 border-b border-border">
              <tr className="text-left">
                <th className="px-3 py-2 label-eyebrow">Data</th>
                <th className="px-3 py-2 label-eyebrow text-right">Intrări</th>
                <th className="px-3 py-2 label-eyebrow text-right">Ieșiri</th>
                <th className="px-3 py-2 label-eyebrow text-right">Stoc</th>
                <th className="px-3 py-2 label-eyebrow">Nr. Lot</th>
                <th className="px-3 py-2 label-eyebrow">Aviz Bio</th>
                <th className="px-3 py-2 label-eyebrow">U.M.</th>
                <th className="px-3 py-2 label-eyebrow">Firma / Referință</th>
                {isAdmin && <th className="px-3 py-2 label-eyebrow text-right">Acțiuni</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((m) => (
                <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{m.data || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">
                    {m.tip === "intrare" ? Number(m.cantitate).toFixed(2) : ""}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-red-700">
                    {m.tip === "iesire" ? Number(m.cantitate).toFixed(2) : ""}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {Number(m.stoc_rezultat).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{m.nr_lot || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{m.aviz || "—"}</td>
                  <td className="px-3 py-2 text-xs">{m.unitate || stock?.unitate || "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    <div>{m.firma || "—"}</div>
                    {m.referinta_numar && (
                      <div className="text-[11px] text-muted-foreground">{m.referinta_numar}</div>
                    )}
                    {m.observatii && (
                      <div className="text-[11px] text-muted-foreground italic">{m.observatii}</div>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => remove(m)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`delete-movement-${m.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {form.tip === "intrare" ? "Adaugă intrare (marfă primită)" : "Adaugă ieșire manuală"}
            </DialogTitle>
            <DialogDescription>
              {form.tip === "intrare"
                ? "Înregistrează o marfă intrată în magazie (aviz furnizor, lot etc.)."
                : "Înregistrează o ieșire care nu vine dintr-un PV (pierderi, testare etc.)."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Tip *</Label>
                <select
                  value={form.tip}
                  onChange={(e) => setForm({ ...form, tip: e.target.value })}
                  className="mt-2 h-11 w-full border border-input rounded-md bg-white px-3 text-sm"
                  data-testid="movement-tip-select"
                >
                  <option value="intrare">Intrare</option>
                  <option value="iesire">Ieșire</option>
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Data *</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  className="mt-2 h-11"
                  required
                  data-testid="movement-data-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Cantitate ({stock?.unitate || "buc"}) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.cantitate}
                  onChange={(e) => setForm({ ...form, cantitate: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="0"
                  required
                  data-testid="movement-cantitate-input"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Nr. Lot</Label>
                <Input
                  value={form.nr_lot}
                  onChange={(e) => setForm({ ...form, nr_lot: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="ex: L2026-01"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Aviz Bio</Label>
                <Input
                  value={form.aviz}
                  onChange={(e) => setForm({ ...form, aviz: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="ex: 1234/2026"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Referință document</Label>
                <Input
                  value={form.referinta}
                  onChange={(e) => setForm({ ...form, referinta: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="ex: Aviz furnizor 45"
                />
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Firma / Furnizor / Client</Label>
              <Input
                value={form.firma}
                onChange={(e) => setForm({ ...form, firma: e.target.value })}
                className="mt-2 h-11"
                placeholder="ex: Bayer, Client X..."
              />
            </div>
            <div>
              <Label className="label-eyebrow">Observații</Label>
              <Input
                value={form.observatii}
                onChange={(e) => setForm({ ...form, observatii: e.target.value })}
                className="mt-2 h-11"
                placeholder="Note interne..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
              <Button
                type="submit"
                disabled={saving}
                className="btn-gold hover:btn-gold"
                data-testid="save-movement-btn"
              >
                {saving ? "Se salvează..." : "Salvează"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
